import {colors} from 'helpers/style'
import {toF} from 'helpers/temperatures'
import _ from 'lodash'
import moment from 'moment'
import {useEffect} from 'react'
import {useState} from 'react'
import ApexCharts from 'react-apexcharts'

function formatTimestamp(timestamp, format = 'YYYY-MM-DD HH:mm:ss') {
  return moment.utc(timestamp).format(format)
}
function selectedTimeRange(temperatures) {
  const format = 'YYYYMMDDTHHmmss'
  const beginTime = formatTimestamp(temperatures[0].timestamp, format)
  const endTime = formatTimestamp(temperatures.slice(-1).pop().timestamp, format)
  if (beginTime.split('T')[0] === endTime.split('T')[0]) {
    return `${beginTime}-${endTime.split('T')[1]}`
  } else {
    return `${beginTime}-${endTime}`
  }
}
const TemperatureChart = ({targetsview, temperatures, setTemperature, height = 500}) => {
  const [data, setData] = useState(null)

  const handleClickOnDataPoint = index => {
    if (index >= 0) {
      setTemperature(temperatures[index])
    }
  }

  const setup = () => {
    const extractTemperatures = (temperatures, accessor, transformer = val => val) =>
      _.map(temperatures, temp => {
        const value = accessor(temp)
        return value !== undefined ? transformer(value) : null
      })

    const tg_warning_f = toF(targetsview.tg_warning)
    const tg_critical_f = toF(targetsview.tg_critical)
    const ambient = extractTemperatures(temperatures, temp => temp.ambient, toF)
    const max = extractTemperatures(temperatures, temp => temp.max, toF)
    const min = extractTemperatures(temperatures, temp => temp.min, toF)
    const mean = extractTemperatures(temperatures, temp => temp.mean, toF)
    const median = extractTemperatures(temperatures, temp => temp.median, toF)

    const categories = _.map(temperatures, temp => moment(temp.timestamp).local().toISOString())

    const allTemperatures = [...ambient, ...max, ...min, ...mean, ...median, tg_warning_f, tg_critical_f].filter(
      Boolean
    )

    const [yaxisMin, yaxisMax] = [
      Math.floor((Math.min(...allTemperatures) - 5) / 5) * 5,
      Math.ceil((Math.max(...allTemperatures) + 5) / 5) * 5
    ]

    const pixelPerTick = 25 // Ideal min space per tick in pixels
    const maxTicks = Math.floor(height / pixelPerTick) // Adjust based on chart height
    const range = yaxisMax - yaxisMin

    // Find the largest tickAmount that is a multiple of 5 and ≤ maxTicks
    let tickAmount = 5
    for (let i = maxTicks; i >= 5; i--) {
      if (i % 5 === 0) {
        tickAmount = i
        break
      }
    }

    // Snap the interval to a multiple of 5 as well
    const interval = Math.ceil(range / tickAmount / 5) * 5
    const adjustedTickAmount = Math.ceil(range / interval)
    const adjustedyaxisMax = yaxisMin + adjustedTickAmount * interval

    const series = {
      data: [
        {
          name: 'Maximum',
          data: max
        },
        {
          name: 'Median',
          data: median
        },
        {
          name: 'Mean',
          data: mean
        },
        {
          name: 'Median',
          data: median
        },
        {
          name: 'Minimum',
          data: min
        }
      ],
      colors: [colors.chart.maximum, colors.chart.mean, colors.chart.median, colors.chart.minimum],
      fillColors: ['transparent', 'transparent', 'transparent', 'transparent']
    }

    if (ambient && ambient.length > 0 && ambient[0]) {
      series.data.push({
        name: 'Ambient',
        data: ambient
      })
      series.colors.push(colors.chart.ambient)
      series.fillColors.push('transparent')
    }

    setData({
      series: series.data,
      options: {
        legend: {
          show: true,
          position: 'top',
          horizontalAlign: 'left',
          floating: false,
          offsetY: 0,
          markers: {
            width: 12,
            height: 12,
            radius: 2
          },
          labels: {
            useSeriesColors: false
          }
        },
        chart: {
          type: 'area',
          events: {
            click(event, chartContext, config) {
              handleClickOnDataPoint(config.dataPointIndex)
            }
          },
          selection: {
            enabled: true
          },
          toolbar: {
            show: true,
            offsetX: 0,
            offsetY: 0,
            tools: {
              download: true,
              selection: false,
              zoom: true,
              zoomin: true,
              zoomout: true,
              pan: true,
              reset: true,
              customIcons: []
            },
            export: {
              csv: {
                filename: `${targetsview.tg_name} ${selectedTimeRange(temperatures)}`,
                headerCategory: 'Timestamp UTC',
                dateFormatter: formatTimestamp
              }
            }
          }
        },
        dataLabels: {
          enabled: false
        },
        colors: series.colors,
        stroke: {
          curve: 'smooth',
          width: 4,
          dashArray: 4
        },
        fill: {
          type: 'solid',
          opacity: 0.3,
          colors: series.fillColors
        },
        xaxis: {
          type: 'datetime',
          categories: categories,
          labels: {
            datetimeUTC: false,
            datetimeFormatter: {
              year: 'yyyy',
              month: "MMM 'yy",
              day: 'MM/dd',
              hour: 'hh:mmTT'
            }
          },
          show: true
        },
        yaxis: [
          {
            // Left side: Fahrenheit (actual data)
            forceNiceScale: false,
            show: true,
            tickAmount: adjustedTickAmount,
            min: yaxisMin,
            max: adjustedyaxisMax,
            labels: {
              formatter: value => Math.round(value) + '°F'
            },
            title: {
              text: 'Temperature (°F)',
              style: {
                color: '#000'
              }
            }
          },
          {
            // Right side: Celsius (converted from Fahrenheit)
            opposite: true,
            show: true,
            min: yaxisMin,
            max: adjustedyaxisMax,
            tickAmount: adjustedTickAmount,
            labels: {
              formatter: value => {
                const celsius = ((value - 32) * 5) / 9
                return Math.round(celsius) + '°C'
              }
            },
            title: {
              text: 'Temperature (°C)',
              style: {
                color: '#000'
              }
            },
            axisBorder: {
              show: true,
              color: '#888'
            },
            axisTicks: {
              show: true,
              color: '#888'
            }
          }
        ],
        tooltip: {
          x: {
            format: 'yyyy/MM/dd HH:mm'
          },
          y: {
            formatter: value => {
              const c = ((value - 32) * 5) / 9
              return `${value.toFixed(1)}°F / ${c.toFixed(1)}°C`
            }
          }
        },
        annotations: {
          yaxis: [
            {
              y: tg_warning_f,
              borderColor: colors.blue40,
              fillColor: colors.blue40,
              strokeDashArray: 10,
              opacity: 0.3,
              label: {
                borderColor: colors.chart.warning,
                style: {
                  color: '#fff',
                  background: colors.chart.warning
                },
                text: 'Warning',
                offsetX: -12, // 👈 move left
                textAnchor: 'end',
                offsetY: 0
              }
            },
            {
              y: tg_critical_f,
              borderColor: colors.blue40,
              fillColor: colors.blue40,
              strokeDashArray: 10,
              opacity: 0.3,
              label: {
                borderColor: colors.chart.critical,
                style: {
                  color: '#fff',
                  background: colors.chart.critical
                },
                text: 'Critical',
                textAnchor: 'end',
                offsetX: -12, // 👈 move left
                offsetY: 0
              }
            }
          ]
        }
      }
    })
  }

  useEffect(() => {
    if (temperatures) setup()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [temperatures])

  if (data) {
    return (
      <div id="temperature-chart" style={{height: `${height}px`}}>
        <ApexCharts options={data.options} series={data.series} type="area" height={height} />
      </div>
    )
  } else {
    return <div id="temperature-chart" style={{height: `${height}px`}}></div>
  }
}

export default TemperatureChart
